import { Button, ErrorMessage, Skeleton } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { openAiCreditsModal } from '@affine/core/components/ai-credits/open';
import {
  buildAiCreditsReturnPath,
  classifyAiCreditsError,
  formatAiCreditsDate,
  formatCreditCount,
  getAiUsageDisplay,
} from '@affine/core/components/ai-credits/utils';
import { UserCopilotQuotaService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { UrlService } from '@affine/core/modules/url';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { cssVar } from '@toeverything/theme';
import { useCallback, useEffect, useState } from 'react';

import * as panelStyles from './ai-usage-panel.css';
import * as styles from './storage-progress.css';

/**
 * Settings -> Account -> AI usage.
 *
 * yoDEV (YOD-627): free allowance, AI Monthly and top-up credits from the
 * YOD-625 ledger; "Buy credits" opens the credits modal and "Manage AI plan"
 * the Stripe Customer Portal. The upstream AFFiNE AI subscription
 * (AISubscribe / billing tab) is not used by yoDEV.
 */
export const AIUsagePanel = () => {
  const t = useI18n();
  const copilotQuota = useService(UserCopilotQuotaService).copilotQuota;
  const workspaceDialogService = useService(WorkspaceDialogService);
  const urlService = useService(UrlService);

  useEffect(() => {
    copilotQuota.revalidate();
  }, [copilotQuota]);
  const quota = useLiveData(copilotQuota.quota$);
  const loadError = useLiveData(copilotQuota.error$);

  const [portalPending, setPortalPending] = useState(false);
  const [portalError, setPortalError] = useState<
    'rate-limited' | 'other' | null
  >(null);

  const buyCredits = useCallback(() => {
    openAiCreditsModal(workspaceDialogService, 'buy');
  }, [workspaceDialogService]);

  const managePlan = useCallback(async () => {
    setPortalPending(true);
    setPortalError(null);
    try {
      const url = await copilotQuota.createPortalSession(
        buildAiCreditsReturnPath(window.location)
      );
      if (!url) throw new Error('Empty portal URL');
      urlService.openExternal(url);
      if (BUILD_CONFIG.isElectron) setPortalPending(false);
    } catch (e) {
      console.error('[ai-credits] portal failed', e);
      setPortalError(classifyAiCreditsError(e));
      setPortalPending(false);
    }
  }, [copilotQuota, urlService]);

  const title = t['com.affine.payment.ai.usage-title']();

  if (!quota) {
    return (
      <SettingRow name={title} desc={''} spreadCol={false}>
        {loadError ? (
          <ErrorMessage>
            {t['com.affine.ai-credits.usage.load-error']()}
          </ErrorMessage>
        ) : (
          <Skeleton height={42} />
        )}
      </SettingRow>
    );
  }

  const display = getAiUsageDisplay(quota);

  if (display.kind === 'unlimited') {
    return (
      <SettingRow
        name={title}
        desc={t['com.affine.ai-credits.usage.unlimited-description']()}
      />
    );
  }

  const lang = t.language;
  const n = (v: number) => formatCreditCount(v, lang);
  const renewDate = formatAiCreditsDate(display.planRenewsAt);
  const resetDate = formatAiCreditsDate(display.resetsAt, { utcDay: true });
  const percent = Math.max(0.5, display.freePercentUsed);
  const color =
    display.totalRemaining === 0 || (display.credits === 0 && percent > 80)
      ? cssVar('errorColor')
      : cssVar('processingColor');

  return (
    <SettingRow name={title} desc={''} spreadCol={false}>
      <div className={styles.storageProgressContainer}>
        <div className={styles.storageProgressWrapper}>
          <div className="storage-progress-desc">
            <span data-testid="ai-usage-free">
              {t['com.affine.ai-credits.usage.free-this-month']({
                used: n(display.freeUsed),
                allowance: n(display.allowance),
              })}
            </span>
            <span>
              {t['com.affine.ai-credits.usage.remaining']({
                remaining: n(display.totalRemaining),
              })}
            </span>
          </div>
          <div className="storage-progress-bar-wrapper">
            <div
              className={styles.storageProgressBar}
              style={{ width: `${percent}%`, backgroundColor: color }}
            />
          </div>
          <div className={panelStyles.details}>
            {display.hasPlan || display.planCredits > 0 ? (
              <span data-testid="ai-usage-plan-credits">
                {renewDate && display.hasPlan
                  ? t['com.affine.ai-credits.usage.plan-credits']({
                      credits: n(display.planCredits),
                      date: renewDate,
                    })
                  : t['com.affine.ai-credits.usage.plan-credits-no-renew']({
                      credits: n(display.planCredits),
                    })}
              </span>
            ) : null}
            {display.topupCredits > 0 ? (
              <span data-testid="ai-usage-topup-credits">
                {t['com.affine.ai-credits.usage.topup-credits']({
                  credits: n(display.topupCredits),
                })}
              </span>
            ) : null}
            {resetDate ? (
              <span>
                {t['com.affine.ai-credits.usage.free-renews']({
                  date: resetDate,
                })}
              </span>
            ) : null}
          </div>
          {portalError ? (
            <ErrorMessage className={panelStyles.error}>
              {portalError === 'rate-limited'
                ? t['com.affine.ai-credits.error.rate-limited']()
                : t['com.affine.ai-credits.error.portal']()}
            </ErrorMessage>
          ) : null}
        </div>

        <div className={panelStyles.actions}>
          <Button
            variant="primary"
            onClick={buyCredits}
            data-testid="ai-usage-buy-credits"
          >
            {t['com.affine.ai-credits.usage.buy']()}
          </Button>
          {display.canManagePlan ? (
            <Button
              loading={portalPending}
              onClick={() => {
                managePlan().catch(console.error);
              }}
              data-testid="ai-usage-manage-plan"
            >
              {t['com.affine.ai-credits.usage.manage-plan']()}
            </Button>
          ) : null}
        </div>
      </div>
    </SettingRow>
  );
};
